import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, Sparkles, Shield, Zap, Clock, Check, TrendingUp, Workflow,
  Mail, MessageSquare, BookOpen, Calendar, X, Plus, Minus, Star, Cpu, GitBranch,
  Boxes, Activity, Lock, Globe, Code2, Users, MessageCircle, Send, Smartphone, Bell,
  BarChart3 as BarChart3WA,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";
import ExitIntentModal from "../components/ExitIntentModal";
import CrispProactive from "../components/CrispProactive";

// ---------------- WHATSAPP HERO DEMO ----------------
const WA_CONVERSATIONS = [
  {
    user: "Envoie la facture à Martin",
    reply: "✅ Facture envoyée à Martin Dubois\n📎 facture-2026-042.pdf\n⏱️ 14:32",
  },
  {
    user: "Relance tous les impayés",
    reply: "✅ 6 relances envoyées\n💰 €4 280 récupérables\n📋 Suivi dans Notion",
  },
  {
    user: "Mon récap de la semaine ?",
    reply: "📊 Semaine du 12 mai\n• 24 workflows exécutés\n• 8h économisées\n• *€280* ROI cette semaine",
  },
];

function WhatsAppHeroDemo() {
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState("user-typing");
  const [typed, setTyped] = useState("");
  const conv = WA_CONVERSATIONS[idx];

  // user typing phase
  useEffect(() => {
    if (phase !== "user-typing") return;
    if (typed.length < conv.user.length) {
      const t = setTimeout(() => setTyped(conv.user.slice(0, typed.length + 1)), 35);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setPhase("sent"), 400);
    return () => clearTimeout(t);
  }, [typed, phase, conv.user]);

  useEffect(() => {
    if (phase === "sent") {
      const t = setTimeout(() => setPhase("processing"), 600);
      return () => clearTimeout(t);
    }
    if (phase === "processing") {
      const t = setTimeout(() => setPhase("replied"), 1500);
      return () => clearTimeout(t);
    }
    if (phase === "replied") {
      const t = setTimeout(() => {
        setTyped("");
        setIdx((i) => (i + 1) % WA_CONVERSATIONS.length);
        setPhase("user-typing");
      }, 3500);
      return () => clearTimeout(t);
    }
  }, [phase]);

  return (
    <div className="relative max-w-sm mx-auto">
      {/* iPhone-ish frame */}
      <div className="bg-black rounded-[2.5rem] p-3 shadow-2xl">
        <div className="bg-[#075E54] rounded-t-[2rem] px-4 pt-6 pb-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center">
            <span className="font-display font-bold text-white text-sm">F</span>
          </div>
          <div>
            <p className="text-white text-sm font-medium">FlowMind AI</p>
            <p className="text-emerald-100 text-[10px] flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-300"/> en ligne
            </p>
          </div>
        </div>
        <div className="bg-[#ECE5DD] min-h-[460px] px-3 py-4 space-y-3 rounded-b-[2rem]">
          {/* User message */}
          {(phase !== "user-typing" || typed) && (
            <motion.div
              key={`u-${idx}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex justify-end"
            >
              <div className="bg-[#DCF8C6] rounded-lg rounded-tr-sm px-3 py-2 max-w-[80%] shadow-sm relative">
                <p className="text-sm">{typed}</p>
                <p className="text-[10px] text-zinc-500 text-right mt-1">
                  14:31 {phase !== "user-typing" && <span className="text-blue-500">✓✓</span>}
                </p>
              </div>
            </motion.div>
          )}

          {/* Processing */}
          {phase === "processing" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
              <div className="bg-white rounded-lg rounded-tl-sm px-3 py-2 shadow-sm flex items-center gap-1">
                {[0, 1, 2].map((i) => (
                  <motion.span
                    key={i}
                    animate={{ y: [0, -3, 0] }}
                    transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }}
                    className="w-1.5 h-1.5 rounded-full bg-zinc-400"
                  />
                ))}
              </div>
            </motion.div>
          )}

          {/* Reply */}
          <AnimatePresence>
            {phase === "replied" && (
              <motion.div
                key={`r-${idx}`}
                initial={{ opacity: 0, y: 8, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0 }}
                className="flex justify-start"
              >
                <div className="bg-white rounded-lg rounded-tl-sm px-3 py-2 max-w-[85%] shadow-sm">
                  <p className="text-sm whitespace-pre-line">{conv.reply}</p>
                  <p className="text-[10px] text-zinc-500 text-right mt-1">14:32</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Floating badge */}
      <motion.div
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 3, repeat: Infinity }}
        className="absolute -top-4 -right-4 bg-emerald-600 text-white px-3 py-1.5 rounded-sm shadow-xl flex items-center gap-1.5"
      >
        <MessageCircle size={12}/>
        <span className="text-[10px] font-mono tracking-wider">CLAUDE 4.5 · LIVE</span>
      </motion.div>
    </div>
  );
}

// ---------------- HERO LIVE AI SIMULATION ----------------
const HERO_PROMPTS = [
  "Quand je reçois un email avec 'facture', classe dans Notion et préviens Slack",
  "Chaque lundi 9h, envoie un récap des leads à l'équipe commerciale",
  "Si un message Slack mentionne 'urgent', crée un ticket Notion prioritaire",
];

const HERO_WORKFLOWS = [
  {
    trigger: { icon: Mail, label: "Email reçu", config: "objet contient 'facture'" },
    steps: [
      { icon: BookOpen, label: "Créer page Notion", config: "Base Factures clients" },
      { icon: MessageSquare, label: "Slack #finance", config: "Notifier l'équipe" },
    ],
    saved: 22,
  },
  {
    trigger: { icon: Clock, label: "Programmation", config: "Lundi 9h00 CET" },
    steps: [
      { icon: Cpu, label: "Agréger leads", config: "Semaine -7 jours" },
      { icon: MessageSquare, label: "Slack #commercial", config: "Récap formaté" },
    ],
    saved: 45,
  },
  {
    trigger: { icon: MessageSquare, label: "Message Slack", config: "contient 'urgent'" },
    steps: [
      { icon: Sparkles, label: "Analyser priorité IA", config: "Claude Sonnet 4.5" },
      { icon: BookOpen, label: "Créer ticket Notion", config: "Priorité haute" },
    ],
    saved: 18,
  },
];

function InteractiveHeroDemo() {
  const [idx, setIdx] = useState(0);
  const [typed, setTyped] = useState("");
  const [phase, setPhase] = useState("typing"); // typing -> generating -> showing
  const targetPrompt = HERO_PROMPTS[idx];

  // Typing animation
  useEffect(() => {
    if (phase !== "typing") return;
    if (typed.length < targetPrompt.length) {
      const t = setTimeout(() => setTyped(targetPrompt.slice(0, typed.length + 1)), 25);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setPhase("generating"), 600);
    return () => clearTimeout(t);
  }, [typed, phase, targetPrompt]);

  // Generating phase
  useEffect(() => {
    if (phase !== "generating") return;
    const t = setTimeout(() => setPhase("showing"), 1400);
    return () => clearTimeout(t);
  }, [phase]);

  // Cycle to next prompt
  useEffect(() => {
    if (phase !== "showing") return;
    const t = setTimeout(() => {
      setTyped("");
      setIdx((i) => (i + 1) % HERO_PROMPTS.length);
      setPhase("typing");
    }, 4200);
    return () => clearTimeout(t);
  }, [phase]);

  const wf = HERO_WORKFLOWS[idx];
  const TriggerIcon = wf.trigger.icon;

  return (
    <div className="relative bg-white border border-zinc-200 rounded-sm overflow-hidden shadow-xl shadow-zinc-900/5">
      {/* Browser bar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-200 bg-zinc-50">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-zinc-300"></span>
          <span className="w-2.5 h-2.5 rounded-full bg-zinc-300"></span>
          <span className="w-2.5 h-2.5 rounded-full bg-zinc-300"></span>
        </div>
        <span className="font-mono text-[10px] text-zinc-400 tracking-wider">flowmindai.eu/new</span>
        <span className="w-12"></span>
      </div>

      {/* Prompt input */}
      <div className="p-5 border-b border-zinc-200 bg-white">
        <p className="overline mb-2">Description du processus</p>
        <div className="font-mono text-sm min-h-[44px] flex items-center">
          <span>{typed}</span>
          <motion.span
            animate={{ opacity: [1, 0] }}
            transition={{ duration: 0.6, repeat: Infinity }}
            className="inline-block w-[2px] h-4 bg-black ml-0.5"
          />
        </div>
      </div>

      {/* AI status */}
      <AnimatePresence mode="wait">
        {phase === "generating" && (
          <motion.div
            key="gen"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="px-5 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center gap-3"
          >
            <div className="w-4 h-4 border-2 border-zinc-300 border-t-black rounded-full animate-spin"/>
            <span className="text-xs text-zinc-600 font-mono">Claude Sonnet 4.5 génère le workflow...</span>
          </motion.div>
        )}
        {phase === "showing" && (
          <motion.div
            key="result"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="px-5 py-3 border-b border-zinc-200 bg-emerald-50 flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <Check size={14} className="text-emerald-600"/>
              <span className="text-xs text-emerald-700 font-mono">Workflow généré en 3,2s</span>
            </div>
            <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">Prêt à exécuter</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Workflow visualization */}
      <div className="p-5 space-y-2 min-h-[260px]">
        <AnimatePresence mode="wait">
          {phase === "showing" && (
            <motion.div key={`flow-${idx}`} className="space-y-2">
              <NodeCard
                accent
                index={0}
                Icon={TriggerIcon}
                label={wf.trigger.label}
                config={wf.trigger.config}
                kind="DÉCLENCHEUR"
              />
              {wf.steps.map((s, i) => (
                <NodeCard
                  key={i}
                  index={i + 1}
                  Icon={s.icon}
                  label={s.label}
                  config={s.config}
                  kind={`ÉTAPE ${i + 1}`}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between">
        <span className="text-[11px] text-zinc-500">Temps économisé / exécution</span>
        <AnimatePresence mode="wait">
          {phase === "showing" && (
            <motion.span
              key={`time-${idx}`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="font-mono font-semibold klein-text"
            >
              {wf.saved} min
            </motion.span>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function NodeCard({ Icon, label, config, kind, accent, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.18, duration: 0.35, ease: "easeOut" }}
      className={`flex items-center gap-3 p-3 rounded-sm border ${accent ? "border-klein bg-blue-50/40" : "border-zinc-200 bg-zinc-50/50"}`}
    >
      <div className={`w-9 h-9 rounded-sm flex items-center justify-center shrink-0 ${accent ? "klein-bg text-white" : "bg-white border border-zinc-200"}`}>
        <Icon size={16} strokeWidth={1.5}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium truncate">{label}</p>
          <span className="overline text-[9px] shrink-0">{kind}</span>
        </div>
        <p className="text-xs text-zinc-500 truncate font-mono">{config}</p>
      </div>
    </motion.div>
  );
}

// ---------------- COMPARISON ----------------
const COMPARISON = [
  { feature: "Création par IA en langage naturel", flowmind: true, zapier: "Beta limitée", n8n: false, make: false },
  { feature: "Données 100% hébergées en UE", flowmind: true, zapier: false, n8n: "Self-host", make: false },
  { feature: "ROI mesuré en € automatiquement", flowmind: true, zapier: false, n8n: false, make: false },
  { feature: "Onboarding < 5 minutes", flowmind: true, zapier: "Complexe", n8n: "Technique", make: "Complexe" },
  { feature: "Support en français", flowmind: true, zapier: "Anglais", n8n: "Communauté", make: "Limité" },
  { feature: "Tarif PME (sous €200/mois)", flowmind: "€49–€399", zapier: "€20–€800+", n8n: "Variable", make: "€10–€500+" },
];

// ---------------- FAQ ----------------
const FAQS = [
  {
    q: "Mes données restent-elles vraiment en Europe ?",
    a: "Oui. L'intégralité de votre stack FlowMind (base de données, exécutions, logs, fichiers) est hébergée dans des datacenters européens certifiés ISO 27001 (région Paris). Aucun transfert hors UE n'est effectué.",
  },
  {
    q: "Faut-il savoir coder pour utiliser FlowMind AI ?",
    a: "Non. Décrivez votre processus en français, et Claude génère le workflow. Pour les ajustements fins, un éditeur visuel à formulaires guidés permet de modifier chaque étape sans une ligne de code.",
  },
  {
    q: "Quelles intégrations sont supportées ?",
    a: "Gmail, Slack, Notion, Google Calendar et Webhooks natifs. Outlook, HubSpot, Linear, Airtable et Salesforce sont sur la roadmap T2 2026. Toute API REST peut déjà être appelée via les webhooks.",
  },
  {
    q: "Comment FlowMind se compare à Zapier ou Make ?",
    a: "FlowMind est spécialement conçu pour les PME européennes : tarif transparent, RGPD natif, génération IA, support en français. Zapier reste excellent en SMB US, n8n parfait pour les techies. Voir notre tableau comparatif.",
  },
  {
    q: "Que se passe-t-il à la fin des 14 jours d'essai ?",
    a: "Aucun prélèvement automatique. Si vous ne choisissez pas de plan, votre compte passe en mode lecture seule. Vous gardez vos workflows pour les réactiver plus tard.",
  },
  {
    q: "Le moteur d'IA est-il vraiment Claude ?",
    a: "Oui, FlowMind utilise Claude Sonnet 4.5 d'Anthropic pour la génération de workflows, via une infrastructure européenne. Aucune donnée n'est utilisée pour l'entraînement de modèles.",
  },
  {
    q: "Puis-je inviter mon équipe ?",
    a: "Oui dès le plan Pro. Vous avez un système de rôles complet (Admin / Editor / Viewer / Billing) avec multi-workspace. Le plan Business débloque le SSO et les audit logs.",
  },
  {
    q: "Comment fonctionne le support en cas de problème ?",
    a: "Support email FR ouvert à tous (réponse < 24h en jours ouvrés). Plan Pro : SLA 4h. Plan Business : ligne Slack privée + ingénieur dédié, SLA 1h. Une question juridique ? Notre DPO (dpo@flowmindai.eu) répond sous 30 jours max.",
  },
];

function FAQItem({ q, a, isOpen, onClick, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ delay: index * 0.05 }}
      className="border-b border-zinc-200"
    >
      <button
        onClick={onClick}
        className="w-full text-left py-6 flex items-start justify-between gap-6"
        data-testid={`faq-toggle-${index}`}
      >
        <span className="font-display text-lg font-medium tracking-tight">{q}</span>
        <span className="shrink-0 mt-1">
          {isOpen ? <Minus size={18}/> : <Plus size={18}/>}
        </span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <p className="text-zinc-600 leading-relaxed pb-6 max-w-3xl">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ---------------- ANIMATED STAT ----------------
function AnimatedNumber({ value, suffix = "", prefix = "" }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        const duration = 1400;
        const start = performance.now();
        const animate = (t) => {
          const p = Math.min((t - start) / duration, 1);
          setN(Math.floor(value * (1 - Math.pow(1 - p, 3))));
          if (p < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
        observer.disconnect();
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);

  return <span ref={ref}>{prefix}{n.toLocaleString("fr-FR")}{suffix}</span>;
}

// ---------------- DATA ----------------
const features = [
  { icon: MessageCircle, title: "Pilotage WhatsApp natif", desc: "Première plateforme à offrir la commande, les alertes et le ROI directement dans WhatsApp.", accent: true },
  { icon: Sparkles, title: "Décris, l'IA construit.", desc: "Expose ton processus en langage naturel. Claude Sonnet 4.5 génère le workflow en quelques secondes." },
  { icon: TrendingUp, title: "ROI mesuré en €", desc: "Analytics hebdomadaires + rapport mensuel PDF envoyé par email et WhatsApp." },
  { icon: Shield, title: "RGPD · Hébergement UE", desc: "Données stockées exclusivement en Union Européenne. Conforme aux normes européennes." },
  { icon: Zap, title: "Intégrations françaises", desc: "Pennylane, Yousign, Google Sheets, Calendly, Shopify — le stack des PME européennes." },
  { icon: Clock, title: "Déployé en 5 minutes", desc: "De la description à l'exécution, onboarding guidé en 3 étapes." },
];

const HOW_IT_WORKS = [
  { step: "01", title: "Décrivez votre besoin", desc: "Une phrase suffit. « Quand un client paie, envoie un email + crée une page Notion. »", icon: Sparkles },
  { step: "02", title: "L'IA construit le workflow", desc: "Claude Sonnet 4.5 traduit votre description en workflow exécutable, étape par étape.", icon: Cpu },
  { step: "03", title: "Activez et mesurez", desc: "Lancez le workflow. FlowMind suit chaque exécution et calcule le ROI en temps réel.", icon: Activity },
];

const USE_CASES = [
  { tag: "Finance", title: "Tri automatique des factures", desc: "Classement, validation, notification à l'équipe finance en moins de 10 secondes." },
  { tag: "Commercial", title: "Suivi des leads chaque lundi", desc: "Récap automatique des nouvelles opportunités, formaté et envoyé à l'équipe." },
  { tag: "Support", title: "Tickets urgents prioritaires", desc: "Détection IA des messages Slack critiques, création de tickets Notion priorisés." },
  { tag: "RH", title: "Onboarding nouveaux salariés", desc: "Envoi des accès, création des comptes, planification des entretiens automatisée." },
];

const TESTIMONIALS = [
  { quote: "FlowMind a remplacé trois outils et nous fait gagner 12h par semaine.", author: "Camille Laurent", role: "COO", company: "Maison Bernard · Paris", initial: "C" },
  { quote: "Décrire un process en français et recevoir le workflow prêt à tourner, c'est bluffant.", author: "Lucas Weber", role: "Dir. Opérations", company: "Zentrum GmbH · Munich", initial: "L" },
  { quote: "Le ROI est mesurable dès la première semaine. Nos investisseurs ont adoré.", author: "Sofia Conti", role: "CEO", company: "Velo & Co · Milan", initial: "S" },
];

const plans = [
  { id: "starter", name: "Starter", price: "49", description: "Pour les équipes qui démarrent l'automatisation.", features: ["3 workflows actifs", "500 exécutions / mois", "Intégrations de base", "Support email"], cta: "Commencer", highlight: false },
  { id: "pro", name: "Pro", price: "149", description: "Pour les PME qui veulent passer à l'échelle.", features: ["15 workflows actifs", "5 000 exécutions / mois", "Toutes les intégrations", "Analytics ROI", "Support prioritaire"], cta: "Choisir Pro", highlight: true },
  { id: "business", name: "Business", price: "399", description: "Pour les organisations qui exigent le meilleur.", features: ["Workflows illimités", "Exécutions illimitées", "SSO + RBAC", "SLA 99,9%", "Onboarding dédié", "Support 24/7"], cta: "Choisir Business", highlight: false },
];

// ---------------- LANDING PAGE ----------------
export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(0);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 80]);
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0.4]);

  const subscribe = async (planId) => {
    if (!user) { navigate(`/signup?plan=${planId}`); return; }
    try {
      const { data } = await api.post("/billing/checkout", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'initier le paiement");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] overflow-x-hidden">
      <PublicHeader />

      {/* ====== HERO ====== */}
      <section className="relative pt-16 pb-32" data-testid="hero-section">
        {/* Animated background grid */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage:
                "linear-gradient(to right, #000 1px, transparent 1px), linear-gradient(to bottom, #000 1px, transparent 1px)",
              backgroundSize: "64px 64px",
              maskImage: "radial-gradient(ellipse 80% 50% at 50% 0%, #000 50%, transparent 100%)",
            }}
          />
          <motion.div
            animate={{ opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 6, repeat: Infinity }}
            className="absolute top-32 right-10 w-72 h-72 rounded-full blur-3xl"
            style={{ background: "radial-gradient(circle, #0038FF22, transparent 70%)" }}
          />
        </div>

        <motion.div
          style={{ y: heroY, opacity: heroOpacity }}
          className="relative max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center"
        >
          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-zinc-300 rounded-full bg-white/80 backdrop-blur-sm mb-8"
            >
              <span className="w-1.5 h-1.5 rounded-full klein-bg animate-pulse"/>
              <span className="text-xs font-mono tracking-wider">CLAUDE SONNET 4.5 · NO-CODE · MADE IN EUROPE</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tighter leading-[0.95] mb-8"
            >
              Automatisez vos processus métier grâce à <span className="klein-text">l'IA</span>.
              <br/>
              <span className="text-zinc-500">Sans coder. En quelques minutes.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="text-lg text-zinc-600 max-w-xl leading-relaxed mb-10"
            >
              Décrivez votre processus en langage naturel, FlowMind AI génère le workflow exécutable.
              <span className="text-black font-medium"> 46 intégrations natives</span>, ROI mesuré en euros, données 100 % hébergées en Europe.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="flex flex-wrap items-center gap-4"
            >
              <Link to="/signup">
                <Button size="lg" className="rounded-sm bg-black text-white hover:bg-zinc-800 px-6 h-12 group" data-testid="hero-cta-signup">
                  Commencer gratuitement
                  <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1"/>
                </Button>
              </Link>
              <Link to="/demo">
                <Button size="lg" variant="outline" className="rounded-sm border-zinc-900 h-12" data-testid="hero-cta-demo">
                  Voir la démo interactive
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-10 flex items-center gap-6 text-xs text-zinc-500 flex-wrap"
            >
              <span className="flex items-center gap-2"><Check size={14} className="klein-text"/> Sans carte bancaire</span>
              <span className="flex items-center gap-2"><Check size={14} className="klein-text"/> RGPD natif</span>
              <span className="flex items-center gap-2"><Check size={14} className="klein-text"/> Déploiement 5 min</span>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="lg:col-span-6"
          >
            <InteractiveHeroDemo />
          </motion.div>
        </motion.div>

        {/* Trust strip */}
        <div className="relative mt-24 border-y border-zinc-200 bg-white">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-wrap items-center justify-between gap-y-4 gap-x-10">
            <p className="overline">Intégrations natives</p>
            <div className="flex items-center gap-10 flex-wrap">
              {[MessageCircle, Mail, MessageSquare, BookOpen, Calendar, Code2].map((Icon, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 6 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className={`flex items-center gap-2 ${i === 0 ? "text-emerald-600" : "text-zinc-700"}`}
                >
                  <Icon size={18} strokeWidth={1.5}/>
                  <span className="font-medium text-sm">{["WhatsApp", "Gmail", "Slack", "Notion", "Calendar", "Webhooks"][i]}</span>
                </motion.div>
              ))}
              <span className="text-xs text-zinc-500 font-mono">+ Pennylane · Yousign · Sheets · Calendly · Shopify</span>
            </div>
          </div>
        </div>
      </section>

      {/* ====== HOW IT WORKS ====== */}
      <section className="py-24" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="overline">Comment ça marche</span>
            <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tighter mt-4">3 étapes. Pas une de plus.</h2>
          </div>
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-px bg-zinc-300"></div>
            {HOW_IT_WORKS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.step}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.12 }}
                  className="relative bg-[#FAFAFA] z-10 text-center"
                >
                  <div className="w-24 h-24 mx-auto rounded-sm bg-white border border-zinc-200 flex items-center justify-center mb-6 relative">
                    <Icon size={28} strokeWidth={1.5}/>
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-black text-white font-mono text-[10px] tracking-wider rounded-sm">{s.step}</span>
                  </div>
                  <h3 className="font-display text-xl font-medium tracking-tight mb-2">{s.title}</h3>
                  <p className="text-zinc-600 text-sm max-w-sm mx-auto">{s.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ====== INTEGRATIONS LOGOS ====== */}
      <section className="py-24 bg-white border-y border-zinc-200" data-testid="integrations-logos-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="overline">46 intégrations natives</span>
            <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tighter mt-4 mb-4">Connectez tout votre stack métier.</h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">
              CRM, comptabilité, e-commerce, RH, signature, marketing — FlowMind orchestre toutes vos applications en quelques clics.
            </p>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3 max-w-6xl mx-auto">
            {[
              { name: "HubSpot", icon: Users, color: "bg-orange-500" },
              { name: "Pipedrive", icon: GitBranch, color: "bg-emerald-500" },
              { name: "Pennylane", icon: Cpu, color: "bg-indigo-500" },
              { name: "Stripe", icon: Code2, color: "bg-violet-500" },
              { name: "Shopify", icon: Boxes, color: "bg-emerald-600" },
              { name: "WhatsApp", icon: MessageCircle, color: "bg-emerald-500" },
              { name: "Slack", icon: MessageSquare, color: "bg-purple-500" },
              { name: "Gmail", icon: Mail, color: "bg-red-500" },
              { name: "Sheets", icon: Workflow, color: "bg-green-500" },
              { name: "Notion", icon: BookOpen, color: "bg-zinc-800" },
              { name: "Calendar", icon: Calendar, color: "bg-blue-500" },
              { name: "Calendly", icon: Calendar, color: "bg-violet-500" },
              { name: "Yousign", icon: Shield, color: "bg-indigo-600" },
              { name: "Mailchimp", icon: Send, color: "bg-yellow-500" },
              { name: "LinkedIn", icon: Users, color: "bg-blue-700" },
              { name: "Zoom", icon: Activity, color: "bg-blue-500" },
            ].map((tool, i) => {
              const Icon = tool.icon;
              return (
                <motion.div
                  key={tool.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ delay: i * 0.03 }}
                  whileHover={{ y: -3 }}
                  className="bg-[#FAFAFA] border border-zinc-200 rounded-sm p-4 flex flex-col items-center gap-2 hover:border-zinc-400 transition-colors"
                >
                  <div className={`w-9 h-9 ${tool.color} rounded-sm flex items-center justify-center text-white shrink-0`}>
                    <Icon size={16} strokeWidth={1.75}/>
                  </div>
                  <p className="text-[11px] font-medium text-zinc-700">{tool.name}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <span className="text-sm text-zinc-500">+ 30 autres intégrations · </span>
            <Link to="/signup" className="text-sm text-black hover:underline font-medium">
              Voir le catalogue complet →
            </Link>
          </div>
        </div>
      </section>

      {/* ====== WHATSAPP SHOWCASE ====== */}
      <section className="py-24 bg-gradient-to-b from-emerald-50/40 to-white border-y border-emerald-100" data-testid="whatsapp-showcase-section">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="overline text-emerald-700">Fonctionnalité signature</span>
            <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tighter mt-4 mb-6 leading-[1.05]">
              Vos automatisations vivent <span className="text-emerald-600">dans votre poche</span>.
            </h2>
            <p className="text-zinc-700 leading-relaxed mb-8">
              Les dirigeants de PME françaises gèrent tout depuis WhatsApp. FlowMind est <strong>la seule plateforme</strong>
              qui s'y intègre nativement : envoyez une commande, recevez le résultat, lisez votre ROI hebdomadaire — sans jamais ouvrir l'app.
            </p>

            <div className="space-y-3 mb-8">
              {[
                { icon: Send, title: "Commande par message", desc: "« Relance les impayés » → exécuté en 3 secondes." },
                { icon: Bell, title: "Alertes intelligentes", desc: "Recevez une notif WhatsApp dès qu'un workflow échoue." },
                { icon: BarChart3WA, title: "ROI hebdo & mensuel", desc: "Récap chaque lundi 9h + rapport PDF le 1er du mois." },
              ].map((f) => {
                const Icon = f.icon;
                return (
                  <div key={f.title} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-sm bg-emerald-600 text-white flex items-center justify-center shrink-0">
                      <Icon size={14}/>
                    </div>
                    <div>
                      <p className="font-medium text-sm">{f.title}</p>
                      <p className="text-xs text-zinc-600">{f.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <Link to="/signup">
              <Button size="lg" className="rounded-sm bg-emerald-600 text-white hover:bg-emerald-700 h-12" data-testid="wa-cta">
                <MessageCircle size={16} className="mr-2"/> Connecter mon WhatsApp
              </Button>
            </Link>
          </div>

          <div className="lg:col-span-7">
            <WhatsAppHeroDemo />
          </div>
        </div>
      </section>

      {/* ====== STATS BAR ====== */}
      <section className="py-20" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { value: 400, suffix: "+", label: "PME européennes" },
            { value: 12, suffix: "h", label: "Économisées / sem. (médiane)" },
            { value: 22000, prefix: "€", label: "ROI moyen / an" },
            { value: 99, suffix: ",9%", label: "SLA garanti" },
          ].map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="border-l border-zinc-300 pl-6"
            >
              <p className="font-display text-5xl font-medium tracking-tighter mb-1">
                <AnimatedNumber value={s.value} suffix={s.suffix} prefix={s.prefix} />
              </p>
              <p className="text-sm text-zinc-500">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ====== FEATURES BENTO ====== */}
      <section id="features" className="py-24 bg-white border-y border-zinc-200" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-5">
              <span className="overline">Ce qui change</span>
              <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tighter mt-4">Une stack unique, pensée pour l'Europe.</h2>
            </div>
            <p className="md:col-span-6 md:col-start-7 text-lg text-zinc-600 leading-relaxed">
              La plupart des outils d'automatisation viennent des US. FlowMind AI est conçu en Europe,
              avec vos contraintes de conformité et votre réalité terrain. Tout ce qu'il faut, rien de plus.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {features.map((f, idx) => {
              const Icon = f.icon;
              const span = idx === 0 ? "md:col-span-6" : idx === 1 ? "md:col-span-6" : "md:col-span-4";
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ delay: (idx % 3) * 0.08 }}
                  whileHover={{ y: -3 }}
                  className={`${span} bg-[#FAFAFA] border border-zinc-200 rounded-sm p-8 transition-all ${f.accent ? "ring-1 ring-black" : ""}`}
                  data-testid={`feature-card-${idx}`}
                >
                  <div className={`w-10 h-10 rounded-sm flex items-center justify-center mb-6 ${f.accent ? "klein-bg" : "bg-white border border-zinc-200"}`}>
                    <Icon size={20} strokeWidth={1.5} className={f.accent ? "text-white" : "text-zinc-700"} />
                  </div>
                  <h3 className="font-display text-2xl font-medium tracking-tight mb-2">{f.title}</h3>
                  <p className="text-zinc-600 leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ====== USE CASES ====== */}
      <section className="py-24" data-testid="use-cases-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <span className="overline">Cas d'usage réels</span>
            <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tighter mt-4 max-w-3xl">Ce que nos clients automatisent.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {USE_CASES.map((u, i) => (
              <motion.div
                key={u.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                whileHover={{ y: -4 }}
                className="bg-white border border-zinc-200 rounded-sm p-6 transition-all"
              >
                <Badge className="bg-black text-white font-mono text-[10px] rounded-sm mb-4">{u.tag.toUpperCase()}</Badge>
                <h3 className="font-display text-xl font-medium tracking-tight mb-2">{u.title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{u.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== PRICING (moved before comparison for better conversion) ====== */}
      <section id="pricing" className="py-24 bg-white border-y border-zinc-200" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="overline">Tarification</span>
            <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tighter mt-4 mb-4">Transparent. Sans surprise.</h2>
            <p className="text-zinc-600 max-w-xl mx-auto">Tous les prix sont en euros HT, facturation mensuelle. Résiliation à tout moment.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`rounded-sm p-8 border flex flex-col transition-all hover:-translate-y-1 ${
                  p.highlight ? "bg-black text-white border-black ring-4 ring-black/5" : "bg-white border-zinc-200"
                }`}
                data-testid={`pricing-card-${p.name.toLowerCase()}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-display text-2xl font-medium tracking-tight">{p.name}</h3>
                  {p.highlight && <Badge className="klein-bg text-white rounded-sm font-mono text-[10px]">POPULAIRE</Badge>}
                </div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="font-display text-5xl font-semibold tracking-tighter">€{p.price}</span>
                  <span className={p.highlight ? "text-zinc-400 text-sm" : "text-zinc-500 text-sm"}>/mois</span>
                </div>
                <p className={`text-sm mb-8 ${p.highlight ? "text-zinc-400" : "text-zinc-600"}`}>{p.description}</p>
                <ul className="space-y-3 mb-8 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check size={16} className={p.highlight ? "text-white mt-0.5 shrink-0" : "klein-text mt-0.5 shrink-0"} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className={`w-full rounded-sm ${p.highlight ? "klein-bg text-white hover:bg-blue-700" : "bg-black text-white hover:bg-zinc-800"}`}
                  data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                  onClick={() => subscribe(p.id)}
                >
                  {p.cta}
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== COMPARISON ====== */}
      <section className="py-24 bg-white border-y border-zinc-200" data-testid="comparison-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="overline">Comparatif</span>
            <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tighter mt-4">Pourquoi pas Zapier, n8n ou Make ?</h2>
            <p className="text-zinc-600 mt-4 max-w-xl mx-auto">Tous excellents. Mais pas conçus pour la réalité PME européenne.</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border border-zinc-200 rounded-sm">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  <th className="text-left p-4 font-display font-medium text-sm">Critère</th>
                  <th className="p-4 font-display font-medium text-sm klein-text">FlowMind AI</th>
                  <th className="p-4 font-display font-medium text-sm text-zinc-500">Zapier</th>
                  <th className="p-4 font-display font-medium text-sm text-zinc-500">n8n</th>
                  <th className="p-4 font-display font-medium text-sm text-zinc-500">Make</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row, i) => (
                  <tr key={row.feature} className="border-b border-zinc-200 last:border-0">
                    <td className="p-4 text-sm text-zinc-700">{row.feature}</td>
                    <td className="p-4 text-center">
                      {row.flowmind === true ? (
                        <Check size={18} className="klein-text mx-auto"/>
                      ) : <span className="text-sm font-medium klein-text">{row.flowmind}</span>}
                    </td>
                    {["zapier", "n8n", "make"].map((k) => (
                      <td key={k} className="p-4 text-center">
                        {row[k] === true ? (
                          <Check size={18} className="text-zinc-400 mx-auto"/>
                        ) : row[k] === false ? (
                          <X size={18} className="text-zinc-300 mx-auto"/>
                        ) : (
                          <span className="text-xs text-zinc-500">{row[k]}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ====== TESTIMONIALS ====== */}
      <section className="py-24" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <span className="overline">Témoignages</span>
            <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tighter mt-4 max-w-3xl">Ils ont arrêté de bricoler.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.figure
                key={t.author}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white border border-zinc-200 rounded-sm p-8 hover:shadow-lg transition-shadow"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={14} className="fill-black text-black"/>
                  ))}
                </div>
                <blockquote className="font-display text-xl leading-snug tracking-tight mb-6">« {t.quote} »</blockquote>
                <figcaption className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full klein-bg text-white flex items-center justify-center font-display font-semibold">
                    {t.initial}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{t.author}</p>
                    <p className="text-xs text-zinc-500">{t.role} · {t.company}</p>
                  </div>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* ====== SECURITY ====== */}
      <section className="py-24 bg-black text-white grain relative overflow-hidden" data-testid="security-section">
        <div className="absolute inset-0 opacity-10 pointer-events-none"
             style={{ backgroundImage: "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)", backgroundSize: "80px 80px" }}/>
        <div className="relative max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <span className="overline text-zinc-400">Sécurité & confidentialité</span>
            <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tighter mt-4 mb-6">
              Vos données restent chez vous. En Europe.
            </h2>
            <p className="text-zinc-400 leading-relaxed">
              Conformité RGPD native, chiffrement de bout en bout, audits ISO 27001 — tout ce qu'exige un DPO européen.
            </p>
          </div>
          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { icon: Globe, title: "Hébergement UE", desc: "Datacenters Paris (eu-west-3). Aucun transfert hors UE." },
              { icon: Lock, title: "Chiffrement AES-256", desc: "Données chiffrées au repos et en transit." },
              { icon: Shield, title: "RGPD natif", desc: "Droit à l'oubli, portabilité, DPO dédié." },
              { icon: Activity, title: "Audits trimestriels", desc: "Pentests + ISO 27001 + SOC 2 (T3 2026)." },
            ].map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.div
                  key={c.title}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="bg-white/5 border border-white/10 rounded-sm p-5 backdrop-blur-sm"
                >
                  <Icon size={20} strokeWidth={1.5} className="mb-3"/>
                  <h4 className="font-display font-medium mb-1">{c.title}</h4>
                  <p className="text-xs text-zinc-400 leading-relaxed">{c.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ====== FAQ ====== */}
      <section className="py-24 bg-white border-y border-zinc-200" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="overline">Questions fréquentes</span>
            <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tighter mt-4">On répond à tout.</h2>
          </div>
          <div>
            {FAQS.map((f, i) => (
              <FAQItem key={i} q={f.q} a={f.a} isOpen={openFaq === i} onClick={() => setOpenFaq(openFaq === i ? -1 : i)} index={i}/>
            ))}
          </div>
        </div>
      </section>

      {/* ====== FINAL CTA ====== */}
      <section className="py-24" data-testid="final-cta-section">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative bg-black text-white rounded-sm p-16 grain overflow-hidden"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
              className="absolute -right-32 -top-32 w-96 h-96 rounded-full opacity-30 pointer-events-none"
              style={{ background: "conic-gradient(from 0deg, #0038FF, transparent, #0038FF)" }}
            />
            <div className="relative text-center">
              <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tighter mb-6">
                Prêt à automatiser votre PME ?
              </h2>
              <p className="text-zinc-300 mb-10 max-w-xl mx-auto">
                Essayez FlowMind AI gratuitement pendant 14 jours. Aucune carte bancaire requise.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link to="/signup">
                  <Button size="lg" className="rounded-sm klein-bg text-white hover:bg-blue-700 h-12 px-6" data-testid="final-cta-signup">
                    Créer mon compte <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
                <Link to="/demo">
                  <Button size="lg" variant="outline" className="rounded-sm border-white text-white bg-transparent hover:bg-white hover:text-black h-12 px-6" data-testid="final-cta-demo">
                    Tester la démo
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <PublicFooter />
      <ExitIntentModal />
      <CrispProactive />
    </div>
  );
}
